import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { setNavigateFunction } from '../lib/axios';
import { useSession, useLogin, useLogout, authKeys } from '../api/auth';
import type { User } from '../api/auth';
import { toUserMessage } from '@/lib/transient';
import i18n from '@/lib/i18n';
import { isPublicAuthPath } from '@/lib/publicPaths';
import { onAuthEvent } from '@/lib/authChannel';
import { loginPathFor } from '@/lib/loginRedirect';
import { showErrorToast } from '@/lib/toast';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  checkSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Public auth pages render immediately and never wait for the session probe;
  // the probe still runs there so a signed-in visitor gets sent home (the pages
  // read isAuthenticated). It answers 401 for everyone else, which the axios
  // layer keeps silent on these pages.
  const isPublicAuthPage = isPublicAuthPath(location.pathname);

  // Use React Query hooks for auth
  const { data: user, isLoading, refetch } = useSession();
  const loginMutation = useLogin();
  const logoutMutation = useLogout();

  // Wrapper to maintain backward compatibility with existing login interface
  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await loginMutation.mutateAsync({ username, password });
      return { success: true };
    } catch (error) {
      // Extract error message for UI display
      // Note: Logging is handled by global mutationCache.onError in queryClient.ts
      return { success: false, error: toUserMessage(error, i18n.t('login.error.failed', 'Login failed')) };
    }
  };

  const logout = () => {
    logoutMutation.mutate();
  };

  // Register navigate function for global 401 handling
  useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);

  // What another tab of this app learned about the session, this tab applies
  // at once (lib/authChannel.ts). The API stays the only authority on WHO is
  // signed in: a sign-in elsewhere makes this tab re-ask, nothing more.
  useEffect(() => {
    return onAuthEvent((event) => {
      if (event.type === 'signed-in') {
        // Every active query re-asks: the session probe flips to the user and
        // the login/register pages send us home; whatever was cached here for
        // whoever was signed in before refreshes too.
        void queryClient.invalidateQueries();
        return;
      }
      // Mirrors the 401 path in lib/axios.ts. "Session expired" only if THIS
      // tab was signed in — and never for a deliberate logout elsewhere. The
      // session is nulled, not removed: its observer is mounted, and removing
      // it would leave this tab deaf to a later sign-in (see Login.tsx).
      if (event.reason === 'expired' && queryClient.getQueryData(authKeys.session())) {
        showErrorToast(i18n.t('toast.sessionExpired', 'Session expired. Please log in again.'));
      }
      queryClient.setQueryData(authKeys.session(), null);
      if (!isPublicAuthPath(window.location.pathname)) {
        // The login URL remembers where THIS tab was (?redirectTo=...)
        navigate(loginPathFor(window.location.pathname + window.location.search));
      }
    });
  }, [queryClient, navigate]);

  const value: AuthContextType = {
    user: user ?? null,
    isAuthenticated: !!user,
    isLoading: isPublicAuthPage ? false : isLoading,
    login,
    logout,
    checkSession: async () => {
      await refetch();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Re-export for convenience
export { authKeys };
export type { User };
